import { SponsoredProductsInventoryId } from 'types/graphql';
import { Category } from 'src/generics/data/generic-category/data.types';
import { useDecorateWithSponsoredProducts } from 'src/utils/ads/hooks/sponsored-products';
import { useAdsEnabledForCategoryPage } from 'src/utils/ads/hooks/use-ads-enabled';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetSortedProductRecommendationsForCategoryPage } from 'src/personalization/utils/hooks';
import { useDecorateCategoryPageWithSponsoredProducts } from 'src/utils/ads/hooks/sponsored-products/use-decorate-category-page-with-sponsored-products';
import useDispensaryProducts from './use-dispensary-products';
import {
  useCategoryProductsForCarouselsQueries,
  CategoryProductsCarouselsQueries,
} from './use-category-products-for-carousels-queries';

type ProductsForCategoryPage = ReturnType<typeof useDispensaryProducts> & {
  productsCarouselQueries: CategoryProductsCarouselsQueries;
};

export function useProductsForCategoryPage({ perPage }: { perPage: number }): ProductsForCategoryPage {
  const { queryParams: qp } = useProductQueryParams();
  const dispensaryCategory = useDispensaryCategory();
  const flags = useFlags();
  const adsEnabledForCategoryPage = useAdsEnabledForCategoryPage();

  const hasBrandsFilter = !!qp.brands.length;

  // The limit we use to fetch ISPs should include our usual prepend limit + any additional placements
  const prependLimit = 5;
  const additionalPlacements: number[] = flags['growth.ads.sponsored-products.additional-category-placements.config']
    ?.placements ?? [12, 42];
  const limit = prependLimit + additionalPlacements.length;

  const category = dispensaryCategory?.value as Category;
  const searchQuery = qp.search;
  const inventoryId = searchQuery
    ? SponsoredProductsInventoryId.searchProductAuc
    : SponsoredProductsInventoryId.categoryProductAuc;

  const isCategoryPage = !searchQuery;

  const productsCarouselQueries = useCategoryProductsForCarouselsQueries({ category });

  const {
    productIds: recommendedProductIds,
    loading: sortedRecommendationsLoading,
  } = useGetSortedProductRecommendationsForCategoryPage({ category });

  const { products: organicProducts, loading, error, ...rest } = useDispensaryProducts({
    filters: { productIds: recommendedProductIds },
    perPage,
    sortBy: !hasBrandsFilter ? qp.sortby : undefined,
  });

  // we only want the new sponsored products result for the category page and if the new flag is enabled
  const shouldUseNewSponsoredProducts =
    isCategoryPage && flags['growth.ads.category-page-sponsored-brand-placements.config'] !== undefined;

  const sponsoredProductsResult = useDecorateWithSponsoredProducts({
    // Skip ads if there is no category on category pages. Search pages should always show ads.
    skip: shouldUseNewSponsoredProducts || !adsEnabledForCategoryPage || (isCategoryPage && !category),
    limit,
    additionalPlacements,
    inventoryId,
    category,
    searchQuery,
    products: organicProducts,
    loading,
    error,
  });

  /**
    TODO: Make sure search page continues to work with useDecorateCategoryPageWithSponsoredProducts
    Details:
      The experiment needs to target the category page only,
      but useProductsForCategoryPage is called on both the search page _and_ the category page.
      If the experiment proves successful and we want to replace
      the call to useDecorateWithSponsoredProducts with a call to useDecorateCategoryPageWithSponsoredProducts,
      we'll need to make sure useDecorateCategoryPageWithSponsoredProducts is called agnostic to the page type
      and continues to serve ads to the search page.

      To do this, we'll simply remove the check for isCategoryPage and only skip the query if ads are not enabled for the category page.
      `skip: !adsEnabledForCategoryPage`
   */
  const newSponsoredProductsResult = useDecorateCategoryPageWithSponsoredProducts({
    skip: !shouldUseNewSponsoredProducts || !adsEnabledForCategoryPage || !category,
    limit,
    inventoryId,
    category,
    searchQuery,
    products: organicProducts,
    loading,
    error,
  });

  const applicableResult = shouldUseNewSponsoredProducts ? newSponsoredProductsResult : sponsoredProductsResult;

  return {
    ...applicableResult,
    ...rest,
    loading: applicableResult.loading || productsCarouselQueries.loading || sortedRecommendationsLoading,
    productsCarouselQueries,
  };
}
